<template>
  <v-data-table
    :headers="headers"
    :items="students"
    :server-items-length="studentCount"
    :options.sync="tableOptions"
    item-key="id"
    sort-by="name"
    :footer-props="tableFooterOptions"
    :loading="loading"
    loading-text="Đang Tải"
  >
    <template #header.name="{ header }">
      <span class="pl-2">
        {{ header.text }}
        <v-icon small>mdi-arrow-down</v-icon>
      </span>
    </template>
    <!-- Tên học sinh -->
    <template #item.name="{ item }">
      <CardStudentName :student="item" link />
    </template>
    <!-- Ngày sinh -->
    <template #item.dob="{ item }">
      <span>{{ item.dob | ddmmyyyy }}</span>
    </template>
    <!-- Giới tính -->
    <template #item.gender="{ item }">{{ item.gender | gender }}</template>
    <!-- Lớp -->
    <template #item.currentClass="{ item }">
      <router-link style="text-decoration: none" :to="'/class/' + (item.currentClass && item.currentClass.id)">
        <span v-if="item.currentClass">{{ item.currentClass | getCurrentClass }}</span>
      </router-link>
    </template>
    <!-- Trạng thái -->
    <template #item.status="{ item }">
      <v-chip small class="white--text" v-if="item.status" :color="getColor(item.status)" label>
        {{ item.status | getStatus }}
      </v-chip>
    </template>
    <!-- Ghi chú -->
    <template #item.notes="{ item }">
      <v-tooltip max-width="250px" bottom>
        <template #activator="{ on, attrs }">
          <p v-bind="attrs" v-on="on" class="nowrap ma-0">{{ item.notes }}</p>
        </template>
        <span>{{ item.notes }}</span>
      </v-tooltip>
    </template>
    <!-- Thao tác -->
    <template #item.action="{ item }">
      <StudentListActions :selectedStudent="item" @success="searchStudents(studentSearchParams)" />
    </template>
  </v-data-table>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { isArray, isEmpty } from 'lodash'
import utils from '@/plugins/utils'
import CardStudentName from '@/components/basic/card/CardStudentName.vue'
import StudentListActions from './StudentListActions'

const defaultHeaders = [
  {
    text: 'Tên học sinh',
    value: 'name',
    align: 'left',
    sortable: false,
    show: true
  },
  {
    text: 'Ngày sinh',
    value: 'dob',
    align: 'left',
    sortable: false,
    show: true
  },
  {
    text: 'Giới tính',
    value: 'gender',
    align: 'left',
    sortable: false,
    show: true,
    width: '100'
  },
  {
    text: 'Lớp',
    value: 'currentClass',
    align: 'left',
    sortable: false,
    show: true,
    width: '100'
  },
  {
    text: 'Trạng thái',
    value: 'status',
    align: 'left',
    sortable: false,
    show: true,
    width: '100'
  },
  {
    text: 'Ghi chú',
    value: 'notes',
    align: 'left',
    sortable: false,
    show: true
  },
  {
    text: 'Thao tác',
    value: 'action',
    align: 'left',
    sortable: false,
    show: true,
    width: '100'
  }
]

export default {
  components: {
    CardStudentName,
    StudentListActions
  },
  data() {
    return {
      headers: isArray(this.customHeaders) && !isEmpty(this.customHeaders) ? this.customHeaders : defaultHeaders,
      tableOptions: {
        page: 1,
        itemsPerPage: 5
      },
      studentSearchParams: {},
      studentCount: 0,
      loading: false
    }
  },
  props: {
    customHeaders: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    ...mapState('students', ['students']),
    tableFooterOptions() {
      let footerOptions = {
        'items-per-page-text': 'Số học sinh mỗi trang',
        'items-per-page-all-text': 'Tất cả',
        'items-per-page': this.tableOptions.itemsPerPage,
        'page-text': utils.paginationText(this.tableOptions, this.studentCount)
      }
      if (this.studentCount > 100) {
        footerOptions['items-per-page-options'] = [5, 10, 15]
      }
      return footerOptions
    }
  },
  async created() {
    await this.searchStudents({ status: 'active' })
  },
  methods: {
    ...mapActions('students', ['getManyStudents', 'getStudentCount']),
    async searchStudents(studentFilters) {
      this.loading = true
      // New filter
      if (studentFilters) {
        this.getStudentCount(studentFilters)
          .then(response => {
            this.studentCount = response
          })
          .catch(error => {
            console.error(error)
            this.$alert.error('Không lấy được số lượng học sinh. Lỗi : ', error)
          })
        this.tableOptions.page = 1
        this.studentSearchParams = { ...studentFilters, ...this.pageSetting() }
      } else {
        this.studentSearchParams = { ...this.studentSearchParams, ...this.pageSetting() }
      }
      try {
        await this.getManyStudents({ searchParams: this.studentSearchParams })
      } catch (error) {
        this.$alert.error('Không lấy được danh sách học sinh. Lỗi : ', error)
        console.error(error)
      }
      this.loading = false
    },
    pageSetting() {
      return {
        _start: (this.tableOptions.page - 1) * this.tableOptions.itemsPerPage,
        _limit: this.tableOptions.itemsPerPage,
        _sort: 'nameToNumber:ASC,index:ASC'
      }
    },
    getColor(status) {
      switch (status) {
        case 'active':
          return '#46BE8A'
        case 'reserved':
          return 'orange'
        case 'graduated':
          return 'primary'
        case 'left':
          return 'red'
        default:
          return 'grey'
      }
    }
  },
  filters: {
    getStatus(status) {
      switch (status) {
        case 'active':
          return 'Đang học'
        case 'reserved':
          return 'Bảo lưu'
        case 'graduated':
          return 'Đã tốt nghiệp'
        case 'left':
          return 'Đã nghỉ'
      }
    },
    getCurrentClass(currentClass) {
      return currentClass == null ? '' : currentClass.title
    }
  },
  watch: {
    tableOptions: {
      handler(newOptions, oldOptions) {
        const itemPerPageChanged = newOptions.itemsPerPage !== oldOptions.itemsPerPage
        const pageChanged = newOptions.page !== oldOptions.page
        if (pageChanged || itemPerPageChanged) {
          this.searchStudents()
        }
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.nowrap {
  white-space: nowrap;
  overflow-x: hidden;
  text-overflow: ellipsis;
  width: 230px;
}
</style>
